import { useEffect, useMemo, useRef, useState } from 'react';
import ShowAllComponent from './ShowAllComponent';
import InfoButton from './InfoButton';
import { useNavigation } from '../function/navigation';
import { buildSearchResults } from '../function/OptionSearch';

const tone = (item, target) =>
  item.searchType === target
    ? 'os-cta'
    : item.available
      ? 'os-primary'
      : 'os-secondary';

const subtitleOf = (item) => {
  if (item.available && (item.name !== null || item.group !== null)) {
    return item.page || '';
  }
  return !item.available && item.name !== null ? 'Not available' : '';
};

function OptionSearchToolbar({
  options,
  amtIndex,
  clickTriggeredRef,
  saveReady,
  svFamily,
}) {
  const [optionSearch, setOptionSearch] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const searchContainerRef = useRef(null);

  const { nav, dispatch } = useNavigation();

  const searchResults = useMemo(
    () => buildSearchResults(optionSearch, options, amtIndex, svFamily),
    [optionSearch, options, amtIndex, svFamily]
  );

  const selectSearchResult = (item) => {
    clickTriggeredRef.current = true;
    dispatch({ type: 'mainPage', payload: 'machine' });
    if (item.page) {
      dispatch({ type: 'currentPage', payload: item.page });
    }
    dispatch({ type: 'focusedInput', payload: null });
    setOptionSearch(item.name || item.group || item.page || '');
    setIsSearchOpen(false);
  };

  const openFeedbackModule = () => {
    dispatch({ type: 'feedbackPanelOpen', payload: true });
    setIsSearchOpen(false);
  };

  useEffect(() => {
    const onClickOutside = (event) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target)
      ) {
        setIsSearchOpen(false);
      }
    };

    document.addEventListener('mousedown', onClickOutside, { passive: true });
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
    };
  }, []);

  return (
    <div className="options-toolbar" ref={searchContainerRef}>
      <div className={`os-wrap ${isSearchOpen ? 'open' : ''}`}>
        <input
          className="shared-search-input"
          type="text"
          placeholder="Find option, value, or group"
          value={optionSearch}
          onFocus={() => setIsSearchOpen(true)}
          onBlur={() => dispatch({ type: 'focusedInput', payload: null })}
          onChange={(event) => {
            setOptionSearch(event.target.value);
            setIsSearchOpen(true);
          }}
        />
        {isSearchOpen && nav.showOptions !== 'all' && (
          <div className="os-warn">
            Some options are hidden by the current setting.
            <button
              type="button"
              className="os-link"
              onClick={() => dispatch({ type: 'showOptions', payload: 'all' })}
            >
              See all options
            </button>
          </div>
        )}
        {isSearchOpen && optionSearch.trim().length >= 2 && (
          <div className="os-list">
            {searchResults.length === 0 ? (
              <div className="os-empty">
                No matching result.
                <button
                  type="button"
                  className="os-link"
                  onClick={openFeedbackModule}
                >
                  Give feedback
                </button>
              </div>
            ) : (
              searchResults.map((item, index) => {
                const title = item.name || item.group || item.page || '';
                const titleTarget = item.name
                  ? 'name'
                  : item.group
                    ? 'group'
                    : 'page';
                const subtitle = subtitleOf(item);

                return (
                  <button
                    key={`${item.searchType}:${item.page || ''}:${item.group || ''}:${item.name || ''}:${item.available}:${index}`}
                    type="button"
                    className="os-item"
                    onClick={() => selectSearchResult(item)}
                  >
                    <div className="os-line">
                      <div className={`os-title ${tone(item, titleTarget)}`}>
                        {title}
                      </div>
                      {subtitle && (
                        <>
                          <div className="os-sep">-</div>
                          {subtitle === 'Not available' ? (
                            <div className="os-sub os-secondary">
                              {subtitle}
                            </div>
                          ) : (
                            <div
                              className={`os-sub ${item.available ? 'os-primary' : 'os-secondary'}`}
                            >
                              {subtitle}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    {item.optionAmtPairs.length > 0 && (
                      <div className="os-meta">
                        {item.optionAmtPairs.map((pair) => (
                          <div
                            key={`${pair.option}:${pair.amtIds.join(',')}`}
                            className="os-detail"
                          >
                            {pair.option !== '__UNPAIRED__' && (
                              <div className={`os-opt ${tone(item, 'option')}`}>
                                {pair.option}
                              </div>
                            )}
                            {pair.amtIds.length > 0 && (
                              <div className={`os-amt ${tone(item, 'amt')}`}>
                                (#{pair.amtIds.join(', #')})
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </button>
                );
              })
            )}
          </div>
        )}
      </div>
      <ShowAllComponent />
      {!isSearchOpen && nav.showOptions === 'priced' && !saveReady && (
        <div className="os-warn-icon">
          <InfoButton
            type="low-warning"
            align="right"
            infoContent={
              <>
                Configuration might be incomplete.
                <br />
                <button
                  type="button"
                  className="os-link"
                  onClick={() =>
                    dispatch({ type: 'showOptions', payload: 'selectable' })
                  }
                >
                  Show selectable options
                </button>
              </>
            }
          />
        </div>
      )}
    </div>
  );
}

export default OptionSearchToolbar;
